/**
 * BRE-9668 (pentest PT-16/17/19): login, consent and OAuth callback URLs carry
 * bearer values in their query (CLI login `state` + loopback `port`, the signed
 * MCP consent `state`, AuthKit `code`, invitation tokens, provider error text).
 * Third-party analytics record the page URL, so they never start on these
 * paths, and every URL they record anywhere else has these params removed.
 * Pure, so the inline gtag snippet and the PostHog `before_send` share one list.
 */
const SENSITIVE_AUTH_PATH_PREFIXES = ["/cli/authorize/", "/mcp/authorize/", "/oauth2/", "/auth/"] as const;

export function isSensitiveAuthPath(pathname: string): boolean {
  const path = pathname.endsWith("/") ? pathname : `${pathname}/`;
  return SENSITIVE_AUTH_PATH_PREFIXES.some((prefix) => path.startsWith(prefix));
}

export const SENSITIVE_URL_PARAMS = [
  "state",
  "code",
  "port",
  "token",
  "invitation_token",
  "error_description",
  "oauth_error_message",
] as const;

const REDACTED_URL_PROPERTIES = [
  "$current_url",
  "$referrer",
  "$initial_current_url",
  "$initial_referrer",
] as const;

// A return target (`/login?returnTo=/mcp/authorize/?state=...`) nests a URL in
// a param; look this many levels deep.
const MAX_NESTED_URL_DEPTH = 3;

function nestedUrl(value: string, base: URL): URL | null {
  if (!/^(\/(?!\/)|https?:\/\/)/i.test(value)) return null;
  try {
    return new URL(value, base.origin);
  } catch {
    return null;
  }
}

function redactParsedUrl(parsed: URL, depth: number): boolean {
  let changed = false;
  for (const param of SENSITIVE_URL_PARAMS) {
    if (parsed.searchParams.has(param)) {
      parsed.searchParams.delete(param);
      changed = true;
    }
  }
  if (depth >= MAX_NESTED_URL_DEPTH) return changed;
  for (const [key, value] of [...parsed.searchParams.entries()]) {
    const nested = nestedUrl(value, parsed);
    if (!nested || !redactParsedUrl(nested, depth + 1)) continue;
    const relative = value.startsWith("/");
    parsed.searchParams.set(key, relative ? `${nested.pathname}${nested.search}${nested.hash}` : nested.toString());
    changed = true;
  }
  return changed;
}

/** `url` without the sensitive params, including inside a nested return URL; a value that is not an absolute URL comes back unchanged. */
export function redactSensitiveUrlParams(url: string): string {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return url;
  }
  return redactParsedUrl(parsed, 0) ? parsed.toString() : url;
}

function isSensitiveParsedLocation(parsed: URL, depth: number): boolean {
  if (isSensitiveAuthPath(parsed.pathname)) return true;
  if (SENSITIVE_URL_PARAMS.some((param) => parsed.searchParams.has(param))) return true;
  if (depth >= MAX_NESTED_URL_DEPTH) return false;
  return [...parsed.searchParams.values()].some((value) => {
    const nested = nestedUrl(value, parsed);
    return nested !== null && isSensitiveParsedLocation(nested, depth + 1);
  });
}

/**
 * True when analytics must not boot on this page: an auth path, or any page
 * whose URL carries an auth param (`/login?code=`, `?invitation_token=`) or
 * returns to such a page (`/login?returnTo=/mcp/authorize/?state=`).
 * PostHog persists the entry URL as initial person info and sends it with
 * flag requests, which never pass through `before_send`.
 */
export function isSensitiveAuthLocation(href: string): boolean {
  let parsed: URL;
  try {
    parsed = new URL(href);
  } catch {
    return false;
  }
  return isSensitiveParsedLocation(parsed, 0);
}

type AnalyticsRecord = Record<string, unknown> | undefined;

function redactUrlProperties(record: AnalyticsRecord): AnalyticsRecord {
  if (!record) return record;
  let redacted: Record<string, unknown> | null = null;
  for (const key of REDACTED_URL_PROPERTIES) {
    const value = record[key];
    if (typeof value !== "string") continue;
    const scrubbed = redactSensitiveUrlParams(value);
    if (scrubbed !== value) (redacted ??= { ...record })[key] = scrubbed;
  }
  return redacted ?? record;
}

/**
 * PostHog `before_send` step: drops an event recorded on a sensitive auth page
 * (the SDK keeps running when a tab navigates client-side onto one), and strips
 * the sensitive params from the URL properties of any other, including the
 * `$initial_*` person properties that remember the session's entry URL.
 */
export function redactSensitiveAuthEvent<
  T extends { properties?: AnalyticsRecord; $set?: AnalyticsRecord; $set_once?: AnalyticsRecord },
>(event: T | null): T | null {
  if (!event) return event;
  const url = event.properties?.$current_url;
  if (typeof url === "string") {
    try {
      if (isSensitiveAuthPath(new URL(url).pathname)) return null;
    } catch {
      // Not a URL: nothing to judge the page by, so keep the event.
    }
  }
  return {
    ...event,
    properties: redactUrlProperties(event.properties),
    ...(event.$set ? { $set: redactUrlProperties(event.$set) } : {}),
    ...(event.$set_once ? { $set_once: redactUrlProperties(event.$set_once) } : {}),
  };
}
